import React from "react";

export const DEFAULT_UNSAVED_CHANGES_MESSAGE = "저장하지 않은 변경사항이 있습니다. 페이지를 떠나시겠습니까?";

type UseUnsavedChangesGuardOptions = {
  isDirty: boolean;
  message?: string;
  enabled?: boolean;
  confirmNavigation: (message: string) => boolean | Promise<boolean>;
};

const isModifiedClick = (event: MouseEvent) => event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;

const findAnchor = (target: EventTarget | null): HTMLAnchorElement | null => {
  if (!(target instanceof Element)) return null;
  return target.closest("a[href]") as HTMLAnchorElement | null;
};

const isSameDocumentHref = (url: URL) =>
  url.pathname === window.location.pathname &&
  url.search === window.location.search &&
  url.hash === window.location.hash;

export function useUnsavedChangesGuard({
  isDirty,
  message = DEFAULT_UNSAVED_CHANGES_MESSAGE,
  enabled = true,
  confirmNavigation,
}: UseUnsavedChangesGuardOptions) {
  const shouldGuard = enabled && isDirty;
  const shouldGuardRef = React.useRef(shouldGuard);
  const messageRef = React.useRef(message);
  const confirmNavigationRef = React.useRef(confirmNavigation);
  const skipNextLinkClickRef = React.useRef(false);

  React.useEffect(() => {
    shouldGuardRef.current = shouldGuard;
    messageRef.current = message;
    confirmNavigationRef.current = confirmNavigation;
  }, [confirmNavigation, message, shouldGuard]);

  const confirmDiscardChanges = React.useCallback(async () => {
    if (!shouldGuardRef.current) return true;
    return confirmNavigationRef.current(messageRef.current);
  }, []);

  React.useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!shouldGuardRef.current) return;
      event.preventDefault();
      event.returnValue = messageRef.current;
      return messageRef.current;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  React.useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (skipNextLinkClickRef.current) {
        skipNextLinkClickRef.current = false;
        return;
      }
      if (!shouldGuardRef.current || event.defaultPrevented || event.button !== 0 || isModifiedClick(event)) {
        return;
      }

      const anchor = findAnchor(event.target);
      if (!anchor || anchor.target || anchor.hasAttribute("download")) return;

      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin || isSameDocumentHref(url)) return;

      event.preventDefault();
      event.stopPropagation();
      void confirmDiscardChanges().then((confirmed) => {
        if (!confirmed) return;
        skipNextLinkClickRef.current = true;
        anchor.click();
      });
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, []);

  React.useEffect(() => {
    const handlePopState = () => {
      if (!shouldGuardRef.current) return;
      void confirmDiscardChanges().then((confirmed) => {
        if (confirmed) return;
        window.history.forward();
      });
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  return { confirmDiscardChanges };
}
